import { createContext, useContext, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import PricingFeatureList from 'components/billing/PricingFeatureList';
import { buildAppPricingPath, PRICING_OPTIONS, SELF_SERVE_PLAN_SLUGS } from 'components/billing/pricingCatalog';
import { useAuth } from 'contexts/AuthContext';
import { apiPost } from 'utils/api';

const PricingDialogContext = createContext({
  openPricingDialog: () => {},
  closePricingDialog: () => {},
  pricingDialogOpen: false
});

function resolveSalesContactUrl(baseUrl) {
  const configuredSalesContactUrl = import.meta.env.VITE_SALES_CONTACT_URL?.trim();
  if (configuredSalesContactUrl) {
    return configuredSalesContactUrl;
  }

  return baseUrl || '/integrations';
}

function formatCheckoutName(user, workspace) {
  const explicitName = typeof user?.name === 'string' && user.name.trim() ? user.name.trim() : '';
  if (explicitName) {
    return explicitName;
  }

  const workspaceName = typeof workspace?.name === 'string' && workspace.name.trim() ? workspace.name.trim() : '';
  if (workspaceName) {
    return workspaceName;
  }

  const email = typeof user?.email === 'string' ? user.email.trim() : '';
  if (!email) {
    return '';
  }

  const [localPart = ''] = email.split('@');
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function PricingDialogProvider({ children }) {
  const { user, workspace } = useAuth();
  const [pricingDialogState, setPricingDialogState] = useState({
    open: false,
    reason: '',
    source: null,
    actionUrl: '',
    planSlug: ''
  });
  const [actionState, setActionState] = useState({ key: null, error: '', success: '' });

  const checkoutCustomerName = useMemo(() => formatCheckoutName(user, workspace), [user, workspace]);
  const checkoutCustomerEmail = typeof user?.email === 'string' ? user.email.trim() : '';

  const openPricingDialog = ({ reason = '', source = null, actionUrl = '', planSlug = '' } = {}) => {
    setActionState({ key: null, error: '', success: '' });
    setPricingDialogState({
      open: true,
      reason,
      source,
      actionUrl,
      planSlug
    });
  };

  const closePricingDialog = () => {
    setPricingDialogState((current) => ({ ...current, open: false }));
  };

  const handlePricingOptionSelect = (planSlug) => {
    if (!SELF_SERVE_PLAN_SLUGS.has(planSlug)) {
      const url = new URL(resolveSalesContactUrl(pricingDialogState.actionUrl), window.location.origin);
      url.searchParams.set('plan', planSlug);
      window.location.assign(url.toString());
      return;
    }

    if (!checkoutCustomerEmail || !checkoutCustomerName) {
      setActionState({
        key: null,
        error: 'We could not find the signed-in buyer name and work email needed for checkout yet.',
        success: ''
      });
      return;
    }

    setActionState({ key: `checkout:${planSlug}`, error: '', success: '' });

    apiPost('/checkout/create', { planSlug })
      .then((response) => {
        if (!response?.checkoutUrl) {
          throw new Error('Checkout is not configured for this workspace yet.');
        }

        window.location.assign(response.checkoutUrl);
      })
      .catch((checkoutError) => {
        setActionState({
          key: null,
          error: checkoutError.message || 'Checkout is not configured for this workspace yet.',
          success: ''
        });
      });
  };

  return (
    <PricingDialogContext.Provider
      value={{
        openPricingDialog,
        closePricingDialog,
        pricingDialogOpen: pricingDialogState.open
      }}
    >
      {children}

      <Dialog open={pricingDialogState.open} onClose={closePricingDialog} fullWidth maxWidth="md">
        <DialogTitle>Choose a plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {pricingDialogState.reason || 'Pick the plan that matches how much capacity you want Liance to keep available inside this workspace.'}
            </Typography>

            {pricingDialogState.source ? (
              <Alert severity="info" variant="outlined">
                {pricingDialogState.source} has reached the included free capacity for this workspace.
              </Alert>
            ) : null}

            {actionState.error ? <Alert severity="error">{actionState.error}</Alert> : null}

            <Button component={RouterLink} to={buildAppPricingPath()} color="secondary" sx={{ alignSelf: 'flex-start' }} onClick={closePricingDialog}>
              Open full pricing page
            </Button>

            <Grid container spacing={1.5} justifyContent="center" alignItems="stretch">
              {PRICING_OPTIONS.map((plan) => {
                const isHighlighted = pricingDialogState.planSlug === plan.slug || (!pricingDialogState.planSlug && plan.featured);

                return (
                  <Grid key={plan.slug} size={{ xs: 12, md: 6, lg: 5 }} sx={{ display: 'flex' }}>
                    <Box
                      sx={{
                        p: 2,
                        height: '100%',
                        width: '100%',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isHighlighted || plan.featured ? 'primary.main' : 'secondary.light',
                        backgroundColor: plan.featured ? 'primary.lighter' : 'grey.0',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography variant="overline" color="primary.main">
                              {plan.audience}
                            </Typography>
                            {isHighlighted ? <Chip label="Recommended" color="primary" size="small" /> : null}
                          </Stack>
                          <Typography variant="h4">{plan.name}</Typography>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                            <Typography variant="h5">{plan.price}</Typography>
                            {plan.priceDetail ? (
                              <Typography variant="body2" color="text.secondary">
                                {plan.priceDetail}
                              </Typography>
                            ) : null}
                          </Stack>
                          {plan.onboarding ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {plan.onboarding}
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {plan.summary}
                        </Typography>
                        <PricingFeatureList title="Included" items={plan.included} />
                        <PricingFeatureList title="Not included" items={plan.excluded} included={false} />
                      </Stack>
                      <Button
                        variant={plan.featured ? 'contained' : 'outlined'}
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={!plan.selectable || actionState.key === `checkout:${plan.slug}`}
                        onClick={() => {
                          if (plan.selectable) {
                            handlePricingOptionSelect(plan.slug);
                          }
                        }}
                      >
                        {actionState.key === `checkout:${plan.slug}` ? 'Starting checkout...' : plan.ctaLabel}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closePricingDialog} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PricingDialogContext.Provider>
  );
}

export function usePricingDialog() {
  return useContext(PricingDialogContext);
}
